import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { Chip } from '@milanote/roma';

import Icon from '../../../components/icons/Icon';
import { useTranslator } from '../../../components/i18n/useTranslator';
import { holdFocusWithoutKeyboard } from '../../../mobile/boardsPage/editing/touchScreenFocusUtils';
import { SheetId } from '../../../mobile/structural/sheet/sheetTypes';
import { selectIsMobileAppMode } from '../../../platform/platformSelector';
import { userLocalePreferenceSelector, userTimeFormatPreferenceSelector } from '../../../user/currentUserSelector';
import { getDueDateClassFromData, getDueDatePillContentFromData } from '../../task/pills/assignment/assignmentUiUtils';
import TaskDueDatePopupContent from '../../task/pills/assignment/dueDate/TaskDueDatePopupContent';
import { REMINDER_VALUES } from '../../task/pills/assignment/dueDate/taskDueDateConstants';
import '../../task/pills/assignment/dueDate/MobileDueDateSheet.scss';
import ChipSelectionPopover from '../selectionViews/ChipSelectionPopover';
import ChipSelectionSheet from '../selectionViews/ChipSelectionSheet';
import { getChipClassNames } from './elementChipClassNames';
import type { ElementChipProps } from './elementChipTypes';

const getDueDateVariant = (dueDateClass: string): 'normal' | 'success' | 'warning' | 'error' => {
    if (dueDateClass === 'completed') return 'normal';
    if (dueDateClass === 'overdue') return 'error';
    if (dueDateClass === 'due-today') return 'warning';

    return 'success';
};

/**
 * Renders a due-date pill and its generic selection-owned editor view.
 */
const DueDateChip: React.FC<Extract<ElementChipProps, { type: 'due-date' }>> = ({
    containerId,
    dueTimestamp,
    hasDueDateTime,
    isComplete,
    dueReminder,
    size,
    isSelected,
    isSearchHighlighted,
    onDeselect,
}) => {
    const translate = useTranslator('elements', 'elementTypes.task');
    const tagRef = useRef<HTMLSpanElement | null>(null);
    const dueDateData = { dueTimestamp, hasDueDateTime, isComplete };
    const dueDateClass = getDueDateClassFromData(dueDateData);
    const hasReminder = dueReminder && dueReminder !== REMINDER_VALUES.NONE;
    const userLocale = useSelector(userLocalePreferenceSelector);
    const timeFormat = useSelector(userTimeFormatPreferenceSelector);
    const isMobileAppMode = useSelector(selectIsMobileAppMode);

    return (
        <>
            <Chip
                ref={tagRef}
                size={size}
                variant={getDueDateVariant(dueDateClass)}
                className={getChipClassNames(isSelected, Boolean(isSearchHighlighted))}
                leading={hasReminder ? <Icon name="reminder-bell-mini" /> : undefined}
            >
                {getDueDatePillContentFromData(dueDateData, translate, userLocale, timeFormat)}
            </Chip>

            {isMobileAppMode ? (
                <ChipSelectionSheet
                    sheetId={SheetId.DueDateTool}
                    instanceId={containerId}
                    className="due-date-tool"
                    isOpen={isSelected}
                    onOpenChange={(isOpen) => !isOpen && onDeselect?.()}
                    onCloseTransitionStart={holdFocusWithoutKeyboard}
                >
                    {(close) => <TaskDueDatePopupContent elementId={containerId} closePopup={close} />}
                </ChipSelectionSheet>
            ) : (
                <ChipSelectionPopover
                    triggerRef={tagRef}
                    isOpen={isSelected}
                    onOpenChange={(isOpen) => !isOpen && onDeselect?.()}
                >
                    {(close) => <TaskDueDatePopupContent elementId={containerId} closePopup={close} />}
                </ChipSelectionPopover>
            )}
        </>
    );
};

export default DueDateChip;
